// src/App.tsx
import React, { JSX } from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import Navbar from "./navbar/Navbar";
import { useTexts } from "./hooks/useTexts";
import LoadingState from "./components/LoadingState";

// Pages
import Home from "./home/Home";
import Chatbot from "./chatbot/Chatbot";
import TaskCreator from "./taskcreator/TaskCreator";
import YouTubeTaskCreator from "./taskcreator/YouTubeTaskCreator";
import KahootTaskCreator from "./taskcreator/KahootTaskCreator";
import Login from "./auth/Login";
import Register from "./auth/Register";
import Account from "./auth/Account";
import CourseList from "./course/CourseList";
import CreateCourse from "./course/CreateCourse";
import CourseDashboard from "./course/dashboard/CourseDashboard";
import WorksheetsPage from "./taskcreator/templateTasks/WorksheetsPage";
import TemplateTaskCreator from "./taskcreator/templateTasks/TemplateTaskCreator";
import TextEditor from "./admin/TextEditor";

/* ================= PROTECTED ROUTE ================= */
function ProtectedRoute({ children }: { children: JSX.Element }) {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
                <div className="text-green-600 text-xl">Lade…</div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return children;
}

/* ================= MAIN APP ================= */
function AppContent() {
    const { loading: textsLoading } = useTexts();

    // Zeige einen globalen Ladebildschirm, bis die Texte geladen sind
    if (textsLoading) {
        return <LoadingState message="Lade Texte..." fullScreen spinnerSize="large" />;
    }

    return (
        <Router>
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-gray-800">
                <Navbar />
                <main>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />

                        {/* Protected Routes */}
                        <Route path="/chatbot" element={<ProtectedRoute><Chatbot /></ProtectedRoute>} />
                        <Route path="/tasks" element={<ProtectedRoute><TaskCreator /></ProtectedRoute>} />
                        <Route path="/youtube-tasks" element={<ProtectedRoute><YouTubeTaskCreator /></ProtectedRoute>} />
                        <Route path="/kahoot-creator" element={<ProtectedRoute><KahootTaskCreator /></ProtectedRoute>} />
                        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                        <Route path="/courses" element={<ProtectedRoute><CourseList /></ProtectedRoute>} />
                        <Route path="/courses/new" element={<ProtectedRoute><CreateCourse /></ProtectedRoute>} />
                        <Route path="/courses/:courseId" element={<ProtectedRoute><CourseDashboard /></ProtectedRoute>} />

                        {/* Worksheets & Templates Routes */}
                        <Route path="/worksheets" element={<ProtectedRoute><WorksheetsPage /></ProtectedRoute>} />
                        <Route path="/worksheets/create/:templateId" element={<ProtectedRoute><TemplateTaskCreator /></ProtectedRoute>} />

                        {/* Admin Text-Editor */}
                        <Route path="/admin/texts" element={<ProtectedRoute><TextEditor /></ProtectedRoute>} />

                        <Route path="/dashboard" element={<ProtectedRoute><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">Dashboard</div></ProtectedRoute>} />
                        <Route path="/settings" element={<ProtectedRoute><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">Einstellungen</div></ProtectedRoute>} />
                    </Routes>
                </main>
            </div>
        </Router>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}